'use client';

import { useEffect, useState } from 'react';
import HeroSection from './HeroSection';
import QuickNavBlock from './QuickNavBlock';
import CyprusDivingSpecial from './CyprusDivingSpecial';
import WhyDiveInCyprus from './WhyDiveInCyprus';
import FeaturedDiveSites from './FeaturedDiveSites';
import PlanYourDive from './PlanYourDive';
import MarineLifeSpotlight from './MarineLifeSpotlight';
import CommunityShowcase from './CommunityShowcase';
import SeasonIndicator from './SeasonIndicator';
import DiveCentresPreview from './DiveCentresPreview';
import HomepageFAQ from './HomepageFAQ';

interface HomepageInteractiveProps {
  className?: string;
}

const HomepageInteractive = ({ className = '' }: HomepageInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className={`min-h-screen ${className}`}>
        <div className="animate-pulse">
          <div className="h-screen bg-muted"></div>
          <div className="py-16 space-y-8">
            <div className="container mx-auto px-4">
              <div className="h-8 bg-muted rounded w-1/3 mx-auto mb-4"></div>
              <div className="h-4 bg-muted rounded w-1/2 mx-auto mb-8"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-64 bg-muted rounded-xl"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* 1. Hero — H1: Scuba Diving in Cyprus */}
      <HeroSection />

      {/* 2. Season Indicator — Best time to dive right now */}
      <SeasonIndicator />

      {/* 3. Quick Navigation Block */}
      <QuickNavBlock />

      {/* 4. Featured Dive Sites — moved higher */}
      <FeaturedDiveSites />

      {/* Subtle divider */}
      <div
        className="flex items-center justify-center py-5"
        style={{ background: '#0B1622' }}
      >
        <div className="flex flex-col items-center gap-1.5">
          <div className="w-px h-6" style={{ background: 'rgba(111,211,224,0.25)' }} />
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(111,211,224,0.35)' }} />
          <div className="w-px h-6" style={{ background: 'rgba(111,211,224,0.25)' }} />
        </div>
      </div>

      {/* 5. Why Dive in Cyprus */}
      <WhyDiveInCyprus />

      {/* 6. Marine Life Spotlight */}
      <MarineLifeSpotlight />

      {/* 7. Dive Centres Preview */}
      <DiveCentresPreview />

      {/* 8. Plan Your Dive / Conditions */}
      <PlanYourDive />

      {/* 9. Community — social proof placeholder */}
      <CommunityShowcase />

      {/* 10. FAQ with schema markup */}
      <HomepageFAQ />

      {/* 11. About This Guide — moved lower */}
      <CyprusDivingSpecial />

      {/* 12. Micro SEO Block — 2-column redesign */}
      <section
        className="py-14 sm:py-16"
        style={{
          background: '#071a24',
          borderTop: '1px solid rgba(255,255,255,0.06)'
        }}
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            {/* Section heading */}
            <h2
              className="text-xl sm:text-2xl font-bold mb-8"
              style={{ color: '#EAF4F8' }}
            >
              Scuba Diving in Cyprus — What You Need to Know
            </h2>

            {/* 2-column layout */}
            <div className="flex flex-col md:flex-row gap-8 md:gap-0">

              {/* LEFT: short paragraph */}
              <div className="md:w-1/2 md:pr-10">
                <p
                  className="text-sm sm:text-base leading-8 max-w-sm"
                  style={{ color: '#A9C3CF' }}
                >
                  <strong style={{ color: '#EAF4F8' }}>Scuba diving in Cyprus</strong> offers some of the most accessible and rewarding diving in the Mediterranean. Warm, clear waters and a wide range of{' '}
                  <strong style={{ color: '#EAF4F8' }}>Cyprus dive sites</strong> — from shallow shore entries to deep wrecks — suit divers of all levels. Whether you&apos;re planning your first dive or your hundredth, Cyprus delivers consistent conditions and memorable encounters year-round.
                </p>

                {/* Zenobia highlight box */}
                <div
                  className="mt-6 rounded-lg p-4"
                  style={{
                    background: 'rgba(111,211,224,0.07)',
                    border: '1px solid rgba(111,211,224,0.25)',
                  }}
                >
                  <p className="text-sm leading-7" style={{ color: '#A9C3CF' }}>
                    <span className="inline-flex items-center gap-1.5 align-middle">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#FF8A3D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 inline-block flex-shrink-0" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    </span>{' '}
                    The{' '}
                    <a
                      href="/dive-sites/zenobia"
                      className="font-semibold underline underline-offset-2 transition-opacity hover:opacity-80"
                      style={{ color: '#6FD3E0' }}
                    >
                      Zenobia wreck
                    </a>
                    {' '}— a 178-metre ferry in Larnaca Bay — is consistently ranked among the{' '}
                    <strong style={{ color: '#EAF4F8' }}>top 10 wreck dives in the world</strong>.
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div
                className="hidden md:block w-px self-stretch mx-2"
                style={{ background: 'rgba(111,211,224,0.15)' }}
              />

              {/* RIGHT: key facts */}
              <div className="md:w-1/2 md:pl-10">
                <ul className="space-y-4">
                  {[
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
                      ),
                      text: (
                        <>
                          <span style={{ color: '#6FD3E0' }} className="font-bold">300+ days</span> of sunshine annually
                        </>
                      ),
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      ),
                      text: (
                        <>
                          Visibility typically <span style={{ color: '#6FD3E0' }} className="font-bold">20–40m</span> in summer
                        </>
                      ),
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0" aria-hidden="true"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>
                      ),
                      text: (
                        <>
                          Water temps <span style={{ color: '#6FD3E0' }} className="font-bold">17–28°C</span> — diving year-round
                        </>
                      ),
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0" aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      ),
                      text: (
                        <>
                          <strong style={{ color: '#EAF4F8' }}>40+ dive sites</strong> — wrecks, reefs, caves &amp; shore dives
                        </>
                      ),
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                      ),
                      text: (
                        <>
                          Rich <strong style={{ color: '#EAF4F8' }}>marine life</strong> — turtles, groupers, octopus &amp; more
                        </>
                      ),
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                      ),
                      text: (
                        <>
                          Suitable for <strong style={{ color: '#EAF4F8' }}>all levels</strong> — beginners to technical divers
                        </>
                      ),
                    },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className="mt-0.5 flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full"
                        style={{ background: 'rgba(111,211,224,0.12)', color: '#6FD3E0' }}
                      >
                        {item.icon}
                      </span>
                      <span className="text-sm sm:text-base leading-7" style={{ color: '#A9C3CF' }}>
                        {item.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomepageInteractive;